import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import ContentCardThumbnail from '../content-card/content-card-thumbnail/content-card-thumbnail';

import './content-layouts.scss';
import FeedHeader, { FeedHeaderProps, FeedViewAllFooter } from './feed-header';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui';
import { Feature, getFeatureFlag } from '../../../models/features/features';
import { Content } from '../../../models/content/types';
import { RootPatronState } from '../../../common/use-patron-selector';
import {
  FeedCardWithData,
  FeedCardProps,
} from '../../content/solar-feed-card/solar-feed-card';
import useScreenSize from '../../../common/use-screen-size';
import useFeatureFlag from '../../../common/use-feature-flag';
import { getContentDetailPath } from '../../../models/content/helpers';

type ContentLayoutGalleryProps = FeedHeaderProps & {
  contents: Content[];
  analyticsData: FeedCardProps['analyticsData'];
  description?: string;
  feedUrl: string;
  viewAllText: string;
  isTopic: boolean;
};

const ContentLayoutGallery = ({
  id,
  contents,
  analyticsData,
  title,
  description,
  feedUrl,
  viewAllText,
  isTopic = false,
}: ContentLayoutGalleryProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const { t } = useTranslation();
  const { isMobile } = useScreenSize();
  const mobileWebParity = useFeatureFlag(
    Feature.CUSTOM_HOMEPAGE_MOBILE_WEB_PARITY
  );
  const useFillCrop = useSelector((state: RootPatronState) =>
    getFeatureFlag(state, Feature.TEST_NEW_UI_FILL_CROP)
  );

  const getImageSrc = (content: Content) =>
    (useFillCrop
      ? content.feed_preview_image_urls?.[0]
      : content.background_image_urls?.[0]?.url) ||
    content.background_image_url;

  const renderThumbnails = (contentArray: Content[]) => {
    return contentArray.map((content, i) => (
      <Button
        className={'gallery-thumbnail' + (i === selectedIndex ? ' active' : '')}
        key={content.id}
        onClick={() => {
          setSelectedIndex(i);
        }}
      >
        <ContentCardThumbnail
          imgSrc={getImageSrc(content)}
          imgSizing={'cover'}
          label={''}
          contentProperties={{
            contentType: content.content_type,
            backgroundColor: content.border_color,
          }}
          aspectRatio={'1:1'}
        />
      </Button>
    ));
  };

  const renderGalleryImage = (content: Content) => (
    <Link
      key={content.id}
      to={getContentDetailPath(content.id)}
      className="content-layout__gallery-mobile-image"
      aria-label={content.title}
    >
      <ContentCardThumbnail
        imgSrc={getImageSrc(content)}
        imgSizing="cover"
        label=""
        contentProperties={{
          contentType: content.content_type,
          backgroundColor: content.border_color,
        }}
        aspectRatio="1:1"
      />
    </Link>
  );

  const renderMobileGallery = () => {
    const topRow = contents.slice(0, 2);
    const bottomRow = contents.slice(2, 5);

    return (
      <div className="content-layout__gallery-mobile">
        <div className="content-layout__gallery-mobile-top">
          {topRow.map(renderGalleryImage)}
        </div>
        {bottomRow.length > 0 && (
          <div className="content-layout__gallery-mobile-bottom">
            {bottomRow.map(renderGalleryImage)}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <FeedHeader
        id={id}
        title={title}
        description={description}
        viewAllLink={feedUrl}
        viewAllText={viewAllText || `${t('assistant.view_all')} ${title}`}
        isTopic={isTopic}
        analyticsData={analyticsData}
        isMobileHorizontal={isMobile && !!mobileWebParity}
      />
      {isMobile && mobileWebParity ? (
        <>
          {renderMobileGallery()}
          {feedUrl && (
            <FeedViewAllFooter
              viewAllLink={feedUrl}
              viewAllText={viewAllText || `${t('assistant.view_all')} ${title}`}
              isTopic={isTopic}
              analyticsData={analyticsData}
            />
          )}
        </>
      ) : (
        <div className={'content-layout__gallery'}>
          <div className={'content-layout__gallery-card'}>
            <FeedCardWithData
              content={contents[selectedIndex]}
              analyticsData={analyticsData}
              compact
              layout="horizontal"
              titleHeadingLevel={3}
            />
          </div>
          <div className={'content-layout__gallery-thumbnails'}>
            {renderThumbnails(contents)}
          </div>
        </div>
      )}
    </>
  );
};

export default ContentLayoutGallery;
